import {
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { isAxiosError } from 'axios';
import { ok } from 'neverthrow';

import {
  CloneCreateResponse,
  CloneJobItemReason,
  CloneJobStatus,
  createLoadClones,
  getLatestLoadCloneJob,
  getLoadCloneJob,
} from 'lib/api/cloneLoads';
import { Maybe } from 'types/UtilityTypes';
import { ApiError } from 'types/api/ApiError';

import { isActiveCloneJob, pollLoadCloneJob } from './loadClonePolling';

export enum LoadCloneJobStateStatus {
  Disabled = 'disabled',
  Discovering = 'discovering',
  Idle = 'idle',
  Starting = 'starting',
  RefreshingRecommendation = 'refreshing-recommendation',
  StartedWithoutJob = 'started-without-job',
  Job = 'job',
  DiscoveryError = 'discovery-error',
  RecommendationReconciliationError = 'recommendation-reconciliation-error',
}

export type LoadCloneJobState =
  | { status: LoadCloneJobStateStatus.Disabled }
  | { status: LoadCloneJobStateStatus.Discovering }
  | { status: LoadCloneJobStateStatus.Idle; startError?: ApiError }
  | { status: LoadCloneJobStateStatus.Starting }
  | { status: LoadCloneJobStateStatus.RefreshingRecommendation }
  | { status: LoadCloneJobStateStatus.StartedWithoutJob }
  | {
      status: LoadCloneJobStateStatus.Job;
      job: CloneCreateResponse;
      refreshError?: ApiError;
      startError?: ApiError;
      isRetrying?: boolean;
    }
  | { status: LoadCloneJobStateStatus.DiscoveryError; error: ApiError }
  | {
      status: LoadCloneJobStateStatus.RecommendationReconciliationError;
      error: ApiError;
    };

type ContextualLoadCloneJobState = {
  contextKey: Maybe<string>;
  state: LoadCloneJobState;
};

type UseLoadCloneJobOptions = {
  enabled: boolean;
  sourceLoadId?: number;
  emailId?: number;
  recommendationId?: number;
  recommendationVersion?: number;
  recommendationStarted?: boolean;
  latestJobId?: Maybe<string>;
  onStarted?: () => void;
  onRecommendationConflict?: () => Promise<boolean>;
};

export type LoadCloneJobController = {
  state: LoadCloneJobState;
  start: () => Promise<boolean>;
  refresh: () => void;
};

const UNKNOWN_DISCOVERY_ERROR: ApiError = {
  message: 'Unable to check load cloning status.',
};
const UNKNOWN_START_ERROR: ApiError = {
  message: 'Unable to start load cloning.',
};
const UNKNOWN_REFRESH_ERROR: ApiError = {
  message: 'Unable to refresh load cloning status.',
};

export function hasUnconfirmedCloneCreation(job: CloneCreateResponse): boolean {
  return job.items.some(
    (item) =>
      item.reason === CloneJobItemReason.AmbiguousWrite ||
      item.reason === CloneJobItemReason.TMSCreateFailed
  );
}

export function canRetryCloneJob(job: CloneCreateResponse): boolean {
  if (hasUnconfirmedCloneCreation(job)) return false;
  return (
    job.status === CloneJobStatus.Failed ||
    (job.status === CloneJobStatus.Partial &&
      (job.summary.failed > 0 || !!job.errors?.length))
  );
}

export function useLoadCloneJob({
  enabled,
  sourceLoadId,
  emailId,
  recommendationId,
  recommendationVersion,
  recommendationStarted = false,
  latestJobId,
  onStarted,
  onRecommendationConflict,
}: UseLoadCloneJobOptions): LoadCloneJobController & {
  handleRecommendationConflict: () => Promise<boolean>;
} {
  const contextKey =
    enabled && sourceLoadId && emailId ? `${sourceLoadId}:${emailId}` : null;
  const contextKeyRef = useRef(contextKey);
  contextKeyRef.current = contextKey;

  const [contextualState, setContextualState] =
    useState<ContextualLoadCloneJobState>(() => ({
      contextKey,
      state: initialStateForContext(contextKey),
    }));
  const state =
    contextualState.contextKey === contextKey
      ? contextualState.state
      : initialStateForContext(contextKey);
  const setState = useCallback(
    (nextState: SetStateAction<LoadCloneJobState>) => {
      setContextualState((current) => {
        const currentState =
          current.contextKey === contextKey
            ? current.state
            : initialStateForContext(contextKey);
        return {
          contextKey,
          state:
            typeof nextState === 'function'
              ? nextState(currentState)
              : nextState,
        };
      });
    },
    [contextKey]
  );
  const [discoveryAttempt, setDiscoveryAttempt] = useState(0);
  const [pollAttempt, setPollAttempt] = useState(0);
  const [fatalError, setFatalError] = useState<Maybe<Error>>(null);
  const startInFlightRef = useRef(false);
  const reconciliationInFlightRef = useRef(false);
  const latestJobRef = useRef<Maybe<CloneCreateResponse>>(null);
  const operationGenerationRef = useRef(0);

  const reconcileRecommendation = useCallback(
    async (requestedContextKey: Maybe<string>, operationGeneration: number) => {
      if (
        contextKeyRef.current !== requestedContextKey ||
        operationGeneration !== operationGenerationRef.current
      )
        return false;
      setState({ status: LoadCloneJobStateStatus.RefreshingRecommendation });
      let refreshed: boolean;
      try {
        refreshed = (await onRecommendationConflict?.()) ?? false;
      } catch {
        refreshed = false;
      }
      if (
        contextKeyRef.current !== requestedContextKey ||
        operationGeneration !== operationGenerationRef.current
      ) {
        return false;
      }
      if (!refreshed) {
        setState({
          status: LoadCloneJobStateStatus.RecommendationReconciliationError,
          error: {
            message:
              'The recommendation changed, but the latest version could not be loaded.',
          },
        });
        return false;
      }
      const startError = {
        message:
          'The source or customer changed. Review the current recommendation and confirm again.',
      };
      setState(
        latestJobRef.current
          ? {
              status: LoadCloneJobStateStatus.Job,
              job: latestJobRef.current,
              startError,
            }
          : { status: LoadCloneJobStateStatus.Idle, startError }
      );
      return false;
    },
    [onRecommendationConflict, setState]
  );

  if (state.status === LoadCloneJobStateStatus.Job) {
    latestJobRef.current = state.job;
  }

  useEffect(() => {
    const operationGeneration = ++operationGenerationRef.current;
    if (!contextKey || !sourceLoadId || !emailId) {
      latestJobRef.current = null;
      setState({ status: LoadCloneJobStateStatus.Disabled });
      return;
    }

    let cancelled = false;
    const requestedContextKey = contextKey;
    latestJobRef.current = null;
    setPollAttempt(0);
    setState({ status: LoadCloneJobStateStatus.Discovering });

    if (recommendationStarted && !latestJobId) {
      setState({ status: LoadCloneJobStateStatus.StartedWithoutJob });
      return;
    }

    const discover = latestJobId
      ? getLoadCloneJob(latestJobId).then((result) =>
          result.isErr() && result.error.status === 404
            ? ok<Maybe<CloneCreateResponse>>(null)
            : result.map((job) => job as Maybe<CloneCreateResponse>)
        )
      : getLatestLoadCloneJob({ sourceLoadId, emailId });

    void discover
      .then((result) => {
        if (cancelled || contextKeyRef.current !== requestedContextKey) {
          return;
        }
        if (operationGeneration !== operationGenerationRef.current) return;
        if (result.isErr()) {
          setState({
            status: LoadCloneJobStateStatus.DiscoveryError,
            error: result.error,
          });
          return;
        }
        if (!result.value) {
          setState({
            status: recommendationStarted
              ? LoadCloneJobStateStatus.StartedWithoutJob
              : LoadCloneJobStateStatus.Idle,
          });
          return;
        }
        latestJobRef.current = result.value;
        setState({ status: LoadCloneJobStateStatus.Job, job: result.value });
      })
      .catch((error: unknown) => {
        if (cancelled || contextKeyRef.current !== requestedContextKey) {
          return;
        }
        if (operationGeneration !== operationGenerationRef.current) return;
        if (isFatalCloneJobError(error)) {
          setFatalError(error);
          return;
        }
        setState({
          status: LoadCloneJobStateStatus.DiscoveryError,
          error: UNKNOWN_DISCOVERY_ERROR,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [
    contextKey,
    discoveryAttempt,
    emailId,
    latestJobId,
    recommendationStarted,
    setState,
    sourceLoadId,
  ]);

  const activeJobID =
    state.status === LoadCloneJobStateStatus.Job && isActiveCloneJob(state.job)
      ? state.job.jobId
      : null;

  useEffect(() => {
    if (!activeJobID || !sourceLoadId || !emailId) {
      return;
    }

    const initialJob = latestJobRef.current;
    if (!initialJob || initialJob.jobId !== activeJobID) {
      return;
    }

    const controller = new AbortController();
    const requestedContextKey = contextKey;

    void pollLoadCloneJob(initialJob, {
      signal: controller.signal,
      pollImmediately: pollAttempt > 0,
      onUpdate: (job) => {
        if (
          controller.signal.aborted ||
          contextKeyRef.current !== requestedContextKey
        ) {
          return;
        }
        latestJobRef.current = job;
        setState({ status: LoadCloneJobStateStatus.Job, job });
      },
    })
      .then(async (outcome) => {
        if (
          controller.signal.aborted ||
          contextKeyRef.current !== requestedContextKey ||
          outcome.type === 'cancelled'
        ) {
          return;
        }
        if (outcome.type === 'not-found') {
          const latestResult = await getLatestLoadCloneJob({
            sourceLoadId,
            emailId,
          });
          if (
            controller.signal.aborted ||
            contextKeyRef.current !== requestedContextKey
          ) {
            return;
          }
          if (latestResult.isErr()) {
            latestJobRef.current = outcome.job;
            setState({
              status: LoadCloneJobStateStatus.Job,
              job: outcome.job,
              refreshError: latestResult.error,
            });
            return;
          }
          if (!latestResult.value) {
            latestJobRef.current = null;
            setState({ status: LoadCloneJobStateStatus.Idle });
            return;
          }
          latestJobRef.current = latestResult.value;
          if (
            isActiveCloneJob(latestResult.value) &&
            latestResult.value.jobId === activeJobID
          ) {
            setState({
              status: LoadCloneJobStateStatus.Job,
              job: latestResult.value,
              refreshError: UNKNOWN_REFRESH_ERROR,
            });
            return;
          }
          setState({
            status: LoadCloneJobStateStatus.Job,
            job: latestResult.value,
          });
          return;
        }
        if (outcome.type === 'error') {
          latestJobRef.current = outcome.job;
          setState({
            status: LoadCloneJobStateStatus.Job,
            job: outcome.job,
            refreshError: outcome.error,
          });
          return;
        }
        latestJobRef.current = outcome.job;
        setState({ status: LoadCloneJobStateStatus.Job, job: outcome.job });
      })
      .catch((error: unknown) => {
        if (
          controller.signal.aborted ||
          contextKeyRef.current !== requestedContextKey
        ) {
          return;
        }
        if (isFatalCloneJobError(error)) {
          setFatalError(error);
          return;
        }
        setState((current) =>
          current.status === LoadCloneJobStateStatus.Job
            ? {
                ...current,
                refreshError: UNKNOWN_REFRESH_ERROR,
              }
            : current
        );
      });

    return () => controller.abort();
  }, [activeJobID, contextKey, emailId, pollAttempt, setState, sourceLoadId]);

  const start = useCallback(async () => {
    if (
      !contextKey ||
      !sourceLoadId ||
      !emailId ||
      (state.status !== LoadCloneJobStateStatus.Idle &&
        !(
          state.status === LoadCloneJobStateStatus.Job &&
          !state.isRetrying &&
          canRetryCloneJob(state.job)
        )) ||
      startInFlightRef.current
    ) {
      return false;
    }

    const requestedContextKey = contextKey;
    const operationGeneration = operationGenerationRef.current;
    const previousJob =
      state.status === LoadCloneJobStateStatus.Job ? state.job : null;
    startInFlightRef.current = true;
    setState(
      previousJob
        ? {
            status: LoadCloneJobStateStatus.Job,
            job: previousJob,
            isRetrying: true,
          }
        : { status: LoadCloneJobStateStatus.Starting }
    );

    try {
      const result = await createLoadClones({
        sourceLoadId,
        emailId,
        idempotencyKey: `load-clones:${sourceLoadId}:${emailId}`,
        recommendationId,
        recommendationVersion,
      });
      if (
        contextKeyRef.current !== requestedContextKey ||
        operationGeneration !== operationGenerationRef.current
      ) {
        return false;
      }

      if (result.isOk()) {
        if (previousJob && result.value.jobId === previousJob.jobId) {
          setState({
            status: LoadCloneJobStateStatus.Job,
            job: result.value,
            startError: {
              message:
                'This attempt could not be restarted. Check the existing result before trying again.',
            },
          });
          return false;
        }
        latestJobRef.current = result.value;
        setState({ status: LoadCloneJobStateStatus.Job, job: result.value });
        onStarted?.();
        return true;
      }

      const latestResult = await getLatestLoadCloneJob({
        sourceLoadId,
        emailId,
      });
      if (
        contextKeyRef.current !== requestedContextKey ||
        operationGeneration !== operationGenerationRef.current
      ) {
        return false;
      }

      if (
        latestResult.isOk() &&
        latestResult.value &&
        latestResult.value.jobId !== previousJob?.jobId
      ) {
        latestJobRef.current = latestResult.value;
        setState({
          status: LoadCloneJobStateStatus.Job,
          job: latestResult.value,
        });
        onStarted?.();
        return true;
      }
      if (recommendationId && result.error.status === 409) {
        return await reconcileRecommendation(
          requestedContextKey,
          operationGeneration
        );
      }
      if (latestResult.isErr()) {
        setState({
          status: LoadCloneJobStateStatus.DiscoveryError,
          error: latestResult.error,
        });
        return false;
      }
      setState(
        previousJob
          ? {
              status: LoadCloneJobStateStatus.Job,
              job: previousJob,
              startError: result.error,
            }
          : { status: LoadCloneJobStateStatus.Idle, startError: result.error }
      );
      return false;
    } catch (error: unknown) {
      if (
        contextKeyRef.current !== requestedContextKey ||
        operationGeneration !== operationGenerationRef.current
      ) {
        return false;
      }
      if (isFatalCloneJobError(error)) {
        setFatalError(error);
        return false;
      }
      setState({
        status: LoadCloneJobStateStatus.DiscoveryError,
        error: UNKNOWN_START_ERROR,
      });
      return false;
    } finally {
      startInFlightRef.current = false;
    }
  }, [
    contextKey,
    emailId,
    onStarted,
    reconcileRecommendation,
    recommendationId,
    recommendationVersion,
    setState,
    sourceLoadId,
    state,
  ]);

  const handleRecommendationConflict = useCallback(async () => {
    if (
      contextKeyRef.current !== contextKey ||
      state.status === LoadCloneJobStateStatus.RefreshingRecommendation ||
      reconciliationInFlightRef.current
    )
      return false;
    reconciliationInFlightRef.current = true;
    const operationGeneration = ++operationGenerationRef.current;
    setState({ status: LoadCloneJobStateStatus.RefreshingRecommendation });
    try {
      return await reconcileRecommendation(contextKey, operationGeneration);
    } finally {
      reconciliationInFlightRef.current = false;
    }
  }, [contextKey, reconcileRecommendation, setState, state.status]);

  const refresh = useCallback(() => {
    if (
      state.status === LoadCloneJobStateStatus.RecommendationReconciliationError
    ) {
      if (reconciliationInFlightRef.current) return;
      reconciliationInFlightRef.current = true;
      const operationGeneration = operationGenerationRef.current;
      setState({ status: LoadCloneJobStateStatus.RefreshingRecommendation });
      void reconcileRecommendation(contextKey, operationGeneration).finally(
        () => {
          reconciliationInFlightRef.current = false;
        }
      );
      return;
    }
    if (state.status === LoadCloneJobStateStatus.DiscoveryError) {
      setDiscoveryAttempt((attempt) => attempt + 1);
      return;
    }
    if (state.status === LoadCloneJobStateStatus.Job && state.refreshError) {
      setState({ status: LoadCloneJobStateStatus.Job, job: state.job });
      setPollAttempt((attempt) => attempt + 1);
    }
  }, [contextKey, reconcileRecommendation, setState, state]);

  if (fatalError) {
    throw fatalError;
  }

  return { state, start, refresh, handleRecommendationConflict };
}

function initialStateForContext(contextKey: Maybe<string>): LoadCloneJobState {
  return contextKey
    ? { status: LoadCloneJobStateStatus.Discovering }
    : { status: LoadCloneJobStateStatus.Disabled };
}

function isFatalCloneJobError(error: unknown): error is Error {
  if (isAxiosError(error) && error.response?.status === 401) {
    return true;
  }
  return (
    error instanceof Error && error.message === 'Extension context invalidated.'
  );
}
